/**
 * @file `useSetChapterMutation` + `useRecordPartAssessmentMutation` —
 *       `POST /api/account/me/chapter` + `.../part-assessment`.
 * @module @labcraft/lab/ui/hooks/use-set-chapter
 *
 * `useSetChapterMutation` persists the URL-derived chapter pointer to
 * the learner's account-global state (ADR-006 (g)). On success it
 * patches the cached `qkMe` learner with the new `currentChapter`
 * pointer so a re-entry into the chapter URL doesn't fire a duplicate
 * POST — **unless the write was soft-gated** (umbrella #1357), in which
 * case the server left the pointer where it was, so the cache must not
 * claim the new one.
 *
 * `useRecordPartAssessmentMutation` persists the terminal Part-level
 * assessment pass the reader banks on the CAT's `pass` verdict — the
 * write side of the soft gate.
 *
 * Convention: hooks live in this directory and never call
 * `useMutation` outside it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { RecordPartAssessmentRequest } from '../../shared/account_routes.ts'
import type { ChapterRef } from '../../shared/ws_messages.ts'
import { qkMe } from './query_keys.ts'
import type { MeData } from './use_me.ts'

/**
 * Persist a new account-global chapter pointer for the signed-in
 * learner. The mutation returns whether the write was soft-gated; its
 * `onSuccess` writes the new pointer into the cached learner (so
 * subsequent reads see it without a `/account/me` refetch) **only when
 * the write was accepted** — a gated write left the server pointer
 * unchanged, so patching the cache would make it lie.
 */
export function useSetChapterMutation() {
  const client = useClient<Lab>()
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, ChapterRef>({
    mutationFn: ref => client.account.setChapter(ref),
    onSuccess: (gated, ref) => {
      if (gated) return
      const prior = queryClient.getQueryData<MeData>(qkMe)
      if (prior?.learner) {
        queryClient.setQueryData<MeData>(qkMe, {
          learner: { ...prior.learner, currentChapter: ref },
        })
      }
    },
  })
}

/**
 * Record the learner's terminal Part-level assessment pass (umbrella
 * #1357). The `PartAssessmentPage` fires this on the CAT's `pass` verdict
 * so a subsequent `setChapter` advance into a Part that lists this Part as
 * a prerequisite clears the soft gate. Idempotent on the server (a re-pass
 * refreshes the row), so no cache patch is needed — the pass isn't part of
 * the cached `/me` shape.
 */
export function useRecordPartAssessmentMutation() {
  const client = useClient<Lab>()
  return useMutation<void, Error, RecordPartAssessmentRequest>({
    mutationFn: pass => client.account.recordPartAssessment(pass),
  })
}
